import { useState, useEffect } from 'react';
import { Bell, AlertCircle, CheckCircle, Clock, ExternalLink, Filter, Loader } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface Alert {
  id: string;
  type: string;
  user_id: string;
  rapport_id: string | null;
  message: string;
  due_at: string;
  statut: 'ouverte' | 'traitee';
  created_at: string;
  user_name: string;
  periode_mois: string | null;
}

export function AlertsManagementPage() {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'ouverte' | 'traitee'>('ouverte');
  const [processing, setProcessing] = useState<string | null>(null);

  useEffect(() => {
    fetchAlerts();
  }, [filter]);

  async function fetchAlerts() {
    setLoading(true);
    try {
      let query = supabase
        .from('alertes')
        .select(`
          *,
          rapports_financiers (
            periode_mois
          )
        `)
        .order('due_at', { ascending: true });

      if (filter !== 'all') {
        query = query.eq('statut', filter);
      }

      const { data: alertsData, error: alertsError } = await query;

      if (alertsError) throw alertsError;

      const enrichedAlerts = await Promise.all(
        (alertsData || []).map(async (alert) => {
          const { data: userData } = await supabase
            .from('user_profiles')
            .select('first_name, last_name, company')
            .eq('id', alert.user_id)
            .maybeSingle();

          const userName = userData?.company ||
            `${userData?.first_name || ''} ${userData?.last_name || ''}`.trim() ||
            'Utilisateur';

          return {
            ...alert,
            user_name: userName,
            periode_mois: alert.rapports_financiers?.periode_mois || null,
          };
        })
      );

      setAlerts(enrichedAlerts);
    } catch (error) {
      console.error('Error fetching alerts:', error);
    } finally {
      setLoading(false);
    }
  }

  async function markAsTreated(alertId: string) {
    setProcessing(alertId);
    try {
      const { error } = await supabase
        .from('alertes')
        .update({ statut: 'traitee' })
        .eq('id', alertId);

      if (error) throw error;

      await fetchAlerts();
    } catch (error: any) {
      console.error('Error marking alert as treated:', error);
      const errorMsg = error?.message || 'Erreur inconnue';
      alert(`Erreur lors de la mise à jour de l'alerte: ${errorMsg}`);
    } finally {
      setProcessing(null);
    }
  }

  function isOverdue(dueAt: string): boolean {
    return new Date(dueAt) < new Date();
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Bell className="text-blue-600" size={32} />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Alertes</h1>
            <p className="text-gray-600">Gestion des notifications administratives</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center gap-2">
          <Filter className="text-gray-400" size={20} />
          <span className="text-sm font-medium text-gray-700">Filtrer par statut:</span>
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                filter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Toutes
            </button>
            <button
              onClick={() => setFilter('ouverte')}
              className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                filter === 'ouverte'
                  ? 'bg-orange-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Ouvertes
            </button>
            <button
              onClick={() => setFilter('traitee')}
              className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                filter === 'traitee'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Traitées
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : alerts.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <Bell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">Aucune alerte trouvée</p>
        </div>
      ) : (
        <div className="space-y-4">
          {alerts.map((alert) => {
            const overdue = alert.statut === 'ouverte' && isOverdue(alert.due_at);

            return (
              <div
                key={alert.id}
                className={`bg-white rounded-lg shadow-sm border-2 p-6 transition-all ${
                  alert.statut === 'ouverte'
                    ? overdue
                      ? 'border-red-300 bg-red-50'
                      : 'border-orange-300 bg-orange-50'
                    : 'border-gray-200 bg-gray-50 opacity-75'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      {alert.statut === 'ouverte' ? (
                        overdue ? (
                          <AlertCircle className="text-red-600 flex-shrink-0" size={24} />
                        ) : (
                          <Clock className="text-orange-600 flex-shrink-0" size={24} />
                        )
                      ) : (
                        <CheckCircle className="text-green-600 flex-shrink-0" size={24} />
                      )}
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{alert.message}</h3>
                        <div className="flex items-center gap-3 mt-1">
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                              alert.statut === 'ouverte'
                                ? 'bg-orange-100 text-orange-800'
                                : 'bg-green-100 text-green-800'
                            }`}
                          >
                            {alert.statut === 'ouverte' ? 'Ouverte' : 'Traitée'}
                          </span>
                          {overdue && (
                            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                              En retard
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-3 text-sm text-gray-600 mb-4">
                      <div>
                        <span className="font-medium">Client:</span> {alert.user_name}
                      </div>
                      {alert.periode_mois && (
                        <div>
                          <span className="font-medium">Période:</span> {alert.periode_mois}
                        </div>
                      )}
                      <div>
                        <span className="font-medium">Échéance:</span>{' '}
                        {new Date(alert.due_at).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </div>
                      <div>
                        <span className="font-medium">Créée le:</span>{' '}
                        {new Date(alert.created_at).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })}
                      </div>
                    </div>

                    {alert.type === 'note_synthese_a_rediger' && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                        <span className="font-medium text-blue-900">Type:</span>{' '}
                        <span className="text-blue-700">Note de synthèse à rédiger</span>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    {alert.rapport_id && (
                      <a
                        href={`#rapport-${alert.rapport_id}`}
                        className="inline-flex items-center gap-1 px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors whitespace-nowrap"
                      >
                        <ExternalLink size={16} />
                        Voir rapport
                      </a>
                    )}
                    {alert.statut === 'ouverte' && (
                      <button
                        onClick={() => markAsTreated(alert.id)}
                        disabled={processing === alert.id}
                        className="inline-flex items-center gap-1 px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 whitespace-nowrap"
                      >
                        {processing === alert.id ? (
                          <>
                            <Loader className="animate-spin" size={16} />
                            Traitement...
                          </>
                        ) : (
                          <>
                            <CheckCircle size={16} />
                            Marquer traitée
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
